@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

:root {
  /* LIGHT THEME VARIABLES (Default) */
  --font-sans: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  --bg-main: #f8fafc;
  --bg-card: rgba(255, 255, 255, 0.8);
  --bg-card-hover: rgba(255, 255, 255, 0.95);
  --bg-inset: rgba(15, 23, 42, 0.04);
  --border-color: rgba(15, 23, 42, 0.08);
  --border-color-active: rgba(99, 102, 241, 0.35);
  --border-subtle: rgba(15, 23, 42, 0.04);
  
  --color-primary: #4f46e5; /* Indigo */
  --color-secondary: #9333ea; /* Purple */
  --color-success: #10b981; /* Emerald */
  --color-warning: #f59e0b; /* Amber */
  --color-danger: #f43f5e; /* Rose */
  
  --text-high: #0f172a;
  --text-medium: #334155;
  --text-muted: #64748b;
  
  --shadow-glow: 0 10px 25px -5px rgba(99, 102, 241, 0.1), 0 8px 10px -6px rgba(99, 102, 241, 0.1);
  --shadow-glow-success: 0 10px 25px -5px rgba(16, 185, 129, 0.1);
  --shadow-glow-danger: 0 10px 25px -5px rgba(244, 63, 94, 0.1);
  --shadow-card: 0 8px 32px 0 rgba(15, 23, 42, 0.06);
  
  --header-bg: #ffffff;
  --simulator-bg: radial-gradient(circle at top, #f1f5f9, #cbd5e1);
  --mobile-device-border: #e2e8f0;
  --mobile-device-bg: #ffffff;
  
  --scrollbar-thumb: rgba(0, 0, 0, 0.15);
  --scrollbar-thumb-hover: rgba(0, 0, 0, 0.25);
  
  --badge-text-success: #059669;
  --badge-text-warning: #d97706;
  --badge-text-danger: #e11d48;
  --badge-text-primary: #4f46e5;
  
  --badge-bg-success: rgba(16, 185, 129, 0.12);
  --badge-bg-warning: rgba(245, 158, 11, 0.12);
  --badge-bg-danger: rgba(244, 63, 94, 0.12);
  --badge-bg-primary: rgba(99, 102, 241, 0.12);
  
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

[data-theme='dark'] {
  /* DARK THEME VARIABLES */
  --bg-main: #060913;
  --bg-card: rgba(13, 18, 33, 0.75);
  --bg-card-hover: rgba(22, 30, 54, 0.85);
  --bg-inset: rgba(255, 255, 255, 0.02);
  --border-color: rgba(255, 255, 255, 0.07);
  --border-color-active: rgba(99, 102, 241, 0.4);
  --border-subtle: rgba(255, 255, 255, 0.03);
  
  --color-primary: #6366f1; /* Indigo */
  --color-secondary: #a855f7; /* Purple */
  --color-success: #10b981; /* Emerald */
  --color-warning: #f59e0b; /* Amber */
  --color-danger: #f43f5e; /* Rose */
  
  --text-high: #f8fafc;
  --text-medium: #cbd5e1;
  --text-muted: #64748b;
  
  --shadow-glow: 0 0 20px rgba(99, 102, 241, 0.15);
  --shadow-glow-success: 0 0 20px rgba(16, 185, 129, 0.15);
  --shadow-glow-danger: 0 0 20px rgba(244, 63, 94, 0.15);
  --shadow-card: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
  
  --header-bg: #090d16;
  --simulator-bg: radial-gradient(circle at top, #0f172a, #020617);
  --mobile-device-border: #1e293b;
  --mobile-device-bg: #090d16;
  
  --scrollbar-thumb: rgba(255, 255, 255, 0.15);
  --scrollbar-thumb-hover: rgba(255, 255, 255, 0.25);
  
  --badge-text-success: #34d399;
  --badge-text-warning: #fbbf24;
  --badge-text-danger: #fb7185;
  --badge-text-primary: #818cf8;
  
  --badge-bg-success: rgba(16, 185, 129, 0.15);
  --badge-bg-warning: rgba(245, 158, 11, 0.15);
  --badge-bg-danger: rgba(244, 63, 94, 0.15);
  --badge-bg-primary: rgba(99, 102, 241, 0.15);
}

body {
  margin: 0;
  padding: 0;
  background-color: var(--bg-main);
  color: var(--text-medium);
  font-family: var(--font-sans);
  min-height: 100vh;
  overflow-x: hidden;
  transition: background-color 0.3s ease, color 0.3s ease;
}

#root {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  box-sizing: border-box;
}

* {
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: var(--scrollbar-thumb) transparent;
}

/* Custom scrollbars */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: var(--scrollbar-thumb);
  border-radius: 4px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--scrollbar-thumb-hover);
}

/* Glassmorphism utility classes */
.glass-panel {
  background: var(--bg-card);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: var(--shadow-card);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.glass-panel-hover:hover {
  background: var(--bg-card-hover);
  border-color: var(--border-color-active);
  box-shadow: var(--shadow-glow);
  transform: translateY(-2px);
}

/* Interactive elements */
.btn-primary {
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  color: #ffffff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.35);
  filter: brightness(1.1);
}

.btn-primary:active {
  transform: translateY(0);
}

.btn-secondary {
  background: var(--bg-inset);
  color: var(--text-medium);
  border: 1px solid var(--border-color);
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background: var(--bg-card-hover);
  border-color: var(--border-color-active);
  color: var(--text-high);
}

.btn-danger {
  background: linear-gradient(135deg, var(--color-danger), #b91c1c);
  color: #ffffff;
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(244, 63, 94, 0.25);
}

.btn-danger:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(244, 63, 94, 0.35);
  filter: brightness(1.1);
}

/* Inputs and Forms */
.form-input, .form-select, .form-textarea {
  background: var(--bg-inset);
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 12px 16px;
  color: var(--text-high);
  font-family: var(--font-sans);
  font-size: 15px;
  outline: none;
  width: 100%;
  transition: all 0.2s ease;
}

.form-input:focus, .form-select:focus, .form-textarea:focus {
  background: var(--bg-card-hover);
  border-color: var(--color-primary);
  box-shadow: 0 0 10px rgba(99, 102, 241, 0.15);
}

/* Badges */
.badge {
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
}

.badge-success {
  background: var(--badge-bg-success);
  color: var(--badge-text-success);
  border: 1px solid rgba(16, 185, 129, 0.2);
}

.badge-warning {
  background: var(--badge-bg-warning);
  color: var(--badge-text-warning);
  border: 1px solid rgba(245, 158, 11, 0.2);
}

.badge-danger {
  background: var(--badge-bg-danger);
  color: var(--badge-text-danger);
  border: 1px solid rgba(244, 63, 94, 0.2);
}

.badge-primary {
  background: var(--badge-bg-primary);
  color: var(--badge-text-primary);
  border: 1px solid rgba(99, 102, 241, 0.2);
}

/* Zebra label ZPL preview styling */
.zpl-preview {
  font-family: 'Courier New', Courier, monospace;
  background: #111827;
  color: #10b981;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid rgba(16, 185, 129, 0.3);
  overflow-x: auto;
  font-size: 13px;
  line-height: 1.4;
}

/* Device Simulator Wrapper */
.simulator-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 80px);
  padding: 20px;
  background: var(--simulator-bg);
  transition: background 0.3s ease;
}

.mobile-device {
  width: 375px;
  height: 812px;
  border: 12px solid var(--mobile-device-border);
  border-radius: 40px;
  background: var(--mobile-device-bg);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 30px rgba(99, 102, 241, 0.1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-device::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 150px;
  height: 25px;
  background: var(--mobile-device-border);
  border-bottom-left-radius: 18px;
  border-bottom-right-radius: 18px;
  z-index: 100;
  transition: background 0.3s ease;
}

.mobile-content {
  flex: 1;
  overflow-y: auto;
  padding: 40px 16px 20px;
  display: flex;
  flex-direction: column;
}

/* Animations */
@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.15);
  }
  50% {
    box-shadow: 0 0 25px rgba(99, 102, 241, 0.3);
  }
}

.pulse-glow {
  animation: pulse-glow 2s infinite ease-in-out;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Forms layout classes */
.form-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-left: 3px solid var(--color-primary);
  padding-left: 14px;
  margin-bottom: 20px;
}

.form-section-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--color-primary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.form-grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
}

.form-row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  border-top: 1px solid var(--border-color);
  padding-top: 16px;
}
